// Caja del taller — Taller → Operación → Caja del taller (962).
// Turno de la caja del taller sin entrar al POS: apertura con fondo, resumen
// (ventas por forma, anticipos y movimientos), ingresos/egresos/retiros,
// Cierre X (parcial) y cierre con arqueo por denominación (Z), e historial con
// reimpresión. Mismo motor y misma contabilización que el POS.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, DoorClosed, Landmark, Loader2, Printer, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, tallerRpc, QK, money, fmtFechaHora, useSort, SortTh, useDialogoEnLinea } from './shared';
import { TurnoInline, useTurnoTaller, type ResumenTurno, type TurnoEstado } from './caja';

type Detalle = ResumenTurno & { cierre?: string | null; efectivo_contado?: number | null; efectivo_sistema_cierre?: number | null; diferencia?: number | null;
  arqueo?: { valor: number; tipo: string; cantidad: number }[] | null; observaciones?: string | null };
type Fila = NonNullable<TurnoEstado['turnos']>[number];

function Linea({ k, v, fuerte = false }: { k: string; v: string; fuerte?: boolean }) {
  return <div className={`flex justify-between gap-2 ${fuerte ? 'font-semibold' : ''}`}><span className="text-muted-foreground">{k}</span><span className="tabular-nums">{v}</span></div>;
}

function Resumen({ r, caja }: { r: ResumenTurno; caja: string }) {
  return (
    <div className="space-y-1 text-sm">
      <Linea k="Caja" v={caja} />
      <Linea k="Turno" v={`${r.codigo} · ${fmtFechaHora(r.fecha_apertura)}${r.cajero ? ` · ${r.cajero}` : ''}`} />
      <Linea k="Fondo inicial" v={money(Number(r.fondo_inicial))} />
      <Linea k="Ventas (entregas, planes, usados)" v={`${r.n_ventas} · ${money(Number(r.total_ventas))}`} />
      {r.formas.length > 0 && <div className="border-t pt-1 text-xs font-semibold uppercase text-muted-foreground">Ventas por forma de pago</div>}
      {r.formas.map((f, i) => <Linea key={i} k={f.nombre} v={money(Number(f.total))} />)}
      <div className="border-t pt-1" />
      <Linea k="Efectivo de ventas (neto de vuelto)" v={money(Number(r.efectivo_ventas))} />
      <Linea k="Ingresos (incluye anticipos en efectivo)" v={money(Number(r.ingresos))} />
      <Linea k="Egresos y retiros" v={money(-Number(r.egresos))} />
      <div className="border-t pt-1"><Linea k="Efectivo esperado en el cajón" v={money(Number(r.efectivo_sistema))} fuerte /></div>
      {r.movimientos.length > 0 && (
        <div className="pt-1">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Movimientos de efectivo</div>
          {r.movimientos.map((m, i) => <Linea key={i} k={`${fmtFechaHora(m.created_at)} · ${m.tipo} · ${m.concepto ?? ''}`} v={money(m.tipo === 'ingreso' ? Number(m.monto) : -Number(m.monto))} />)}
        </div>
      )}
    </div>
  );
}

export function CajaTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isLoading } = useTurnoTaller();
  const [vista, setVista] = useState<'turno' | 'x' | 'cerrar' | { z: string }>('turno');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [obs, setObs] = useState('');
  const [mov, setMov] = useState({ tipo: 'retiro', monto: '', concepto: '' });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  const movimiento = useMutation({
    mutationFn: () => tallerRpc('turno_movimiento', { _tipo: mov.tipo, _monto: Number(mov.monto), _concepto: mov.concepto }),
    onSuccess: () => { toast.success('Movimiento registrado'); setMov({ tipo: 'retiro', monto: '', concepto: '' }); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const denoms = useMemo(() => data?.denominaciones ?? [], [data?.denominaciones]);
  const contado = useMemo(() => denoms.reduce((s, d) => s + Number(d.valor) * (counts[d.id] || 0), 0), [denoms, counts]);
  const cerrar = useMutation({
    mutationFn: () => tallerRpc<{ sesion_id: string; diferencia: number; dentro_tolerancia: boolean }>('turno_cerrar', {
      _contado: Math.round(contado * 100) / 100,
      _detalle: denoms.filter((d) => counts[d.id]).map((d) => ({ valor: d.valor, tipo: d.tipo, cantidad: counts[d.id] })),
      _observaciones: obs || null,
    }),
    onSuccess: (r) => {
      toast.success(r.dentro_tolerancia ? 'Turno cerrado y cuadrado' : `Turno cerrado con diferencia de ${money(Number(r.diferencia))}: queda para revisión`);
      setCounts({}); setObs(''); setVista({ z: r.sesion_id }); inv();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !data) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  if (!data.activado) return <div className="p-6"><TurnoInline /></div>;
  const cajaTxt = `${data.caja?.nombre} (${data.caja?.serie})`;
  const t = data.turno;

  if (typeof vista === 'object') return <ReimpresionZ id={vista.z} caja={cajaTxt} onVolver={() => setVista('turno')} />;

  if (vista === 'x' && t) {
    return (
      <div className="mx-auto max-w-md space-y-3 p-4">
        <div className="no-print flex items-center gap-2"><Button size="sm" variant="outline" onClick={() => setVista('turno')}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Button><PrintButton /></div>
        <Imprimible titulo="Cierre X (parcial, el turno sigue abierto)" subtitulo={t.codigo}><Resumen r={t} caja={cajaTxt} /></Imprimible>
      </div>
    );
  }

  const faltanCuentas = data.cuentas_cierre && (!data.cuentas_cierre.faltante_caja || !data.cuentas_cierre.sobrante_caja || !data.cuentas_cierre.ventas);

  return (
    <div className="max-w-5xl space-y-4 p-4 sm:p-6">
      {dlg.ui}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Landmark className="h-5 w-5" /> Caja del taller · {cajaTxt}</h1>
        <p className="text-sm text-muted-foreground">Turno de caja del taller: lo que se cobra en efectivo (entregas y anticipos) debe cuadrar al cerrar.</p>
      </div>
      {faltanCuentas && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Falta mapear en Contabilidad {[
            !data.cuentas_cierre!.faltante_caja && '«Faltante de caja»', !data.cuentas_cierre!.sobrante_caja && '«Sobrante de caja»', !data.cuentas_cierre!.ventas && 'ventas / IVA en ventas',
          ].filter(Boolean).join(', ')}: el cierre se registra igual, pero su asiento queda en la bandeja de excepciones hasta mapearlas.
        </div>
      )}

      {!t ? <TurnoInline motivo="No hay un turno abierto en la caja del taller." /> : vista === 'cerrar' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Resumen del turno</CardTitle></CardHeader><CardContent><Resumen r={t} caja={cajaTxt} /></CardContent></Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><DoorClosed className="h-5 w-5" /> Arqueo y cierre (Z)</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">Cuente el efectivo del cajón por denominación (sin mirar el esperado).</p>
              <table className="w-full text-sm"><tbody>
                {denoms.map((d) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="py-1 pr-2">{money(Number(d.valor))} <span className="text-xs capitalize text-muted-foreground">{d.tipo}</span></td>
                    <td className="w-24 py-1 pr-2"><input type="number" min={0} className={`${fieldCls} h-8 text-right`} value={counts[d.id] || ''}
                      onChange={(x) => setCounts((c) => ({ ...c, [d.id]: Math.max(0, parseInt(x.target.value) || 0) }))} /></td>
                    <td className="py-1 text-right tabular-nums">{money(Number(d.valor) * (counts[d.id] || 0))}</td>
                  </tr>))}
              </tbody></table>
              {denoms.length === 0 && <p className="text-xs text-muted-foreground">Sin denominaciones configuradas: se crean al abrir el siguiente turno.</p>}
              <div className="space-y-1 border-t pt-2">
                <Linea k="Efectivo contado" v={money(contado)} fuerte />
                <Linea k="Efectivo esperado" v={money(Number(t.efectivo_sistema))} />
                <div className={`flex justify-between font-bold ${Math.abs(contado - Number(t.efectivo_sistema)) < 0.005 ? 'text-emerald-600' : 'text-red-600'}`}>
                  <span>Diferencia</span><span className="tabular-nums">{money(Math.round((contado - Number(t.efectivo_sistema)) * 100) / 100)}</span></div>
              </div>
              <Campo label="Observaciones"><input className={fieldCls} value={obs} onChange={(x) => setObs(x.target.value)} /></Campo>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setVista('turno')}>Cancelar</Button>
                <Button size="sm" isLoading={cerrar.isPending} onClick={async () => {
                  if (await dlg.confirmar('¿Cerrar el turno de la caja del taller?', { detalle: `Contado ${money(contado)} · esperado ${money(Number(t.efectivo_sistema))}. Después del cierre no se puede entregar ni cobrar en efectivo hasta abrir otro turno.`, aceptar: 'Cerrar turno' })) cerrar.mutate();
                }}>Cerrar turno</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Wallet className="h-5 w-5" /> Turno {t.codigo} abierto</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Resumen r={t} caja={cajaTxt} />
              {data.puede_operar && <div className="flex flex-wrap justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setVista('x')}><Printer className="mr-1 h-4 w-4" /> Cierre X</Button>
                <Button size="sm" onClick={() => setVista('cerrar')}><DoorClosed className="mr-1 h-4 w-4" /> Arqueo y cierre</Button>
              </div>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Movimiento de efectivo</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">Retiro (depósito al banco), egreso (gasto menor) o ingreso que no es venta. Los anticipos en efectivo entran solos.</p>
              <select className={fieldCls} disabled={!data.puede_operar} value={mov.tipo} onChange={(x) => setMov({ ...mov, tipo: x.target.value })}>
                <option value="retiro">Retiro / depósito</option><option value="egreso">Egreso (gasto)</option><option value="ingreso">Ingreso</option>
              </select>
              <input type="number" min={0} step="0.01" className={fieldCls} disabled={!data.puede_operar} placeholder="Monto" value={mov.monto} onChange={(x) => setMov({ ...mov, monto: x.target.value })} />
              <input className={fieldCls} disabled={!data.puede_operar} placeholder="Motivo" value={mov.concepto} onChange={(x) => setMov({ ...mov, concepto: x.target.value })} />
              <Button size="sm" className="w-full" disabled={!data.puede_operar || !Number(mov.monto) || mov.concepto.trim().length < 3} isLoading={movimiento.isPending} onClick={() => movimiento.mutate()}>Registrar</Button>
              {!data.puede_operar && <p className="text-xs text-amber-700">Usted no es cajero de esta caja.</p>}
            </CardContent>
          </Card>
        </div>
      )}

      <Historial turnos={data.turnos ?? []} onAbrir={(id) => setVista({ z: id })} />
    </div>
  );
}

function Historial({ turnos, onAbrir }: { turnos: Fila[]; onAbrir: (id: string) => void }) {
  const sort = useSort<Fila>(turnos, (r, k) => (k === 'codigo' ? r.codigo : k === 'apertura' ? r.apertura : k === 'ventas' ? Number(r.total_ventas ?? 0) : k === 'dif' ? Number(r.diferencia ?? 0) : null));
  const tot = turnos.reduce((a, r) => ({ v: a.v + Number(r.total_ventas ?? 0), d: a.d + Number(r.diferencia ?? 0) }), { v: 0, d: 0 });
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Turnos cerrados (últimos 15)</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><SortTh col="codigo" sort={sort}>Turno</SortTh><SortTh col="apertura" sort={sort}>Apertura</SortTh>
            <th className="py-2 pr-2">Cierre</th><th className="py-2 pr-2">Cajero</th><SortTh col="ventas" sort={sort} className="text-right">Ventas</SortTh>
            <th className="py-2 pr-2 text-right">Esperado</th><th className="py-2 pr-2 text-right">Contado</th><SortTh col="dif" sort={sort} className="text-right">Diferencia</SortTh><th /></tr></thead>
          <tbody>
            {turnos.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">Aún no hay turnos cerrados.</td></tr>}
            {sort.sorted.map((r, i) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2 font-mono">{r.codigo}</td>
                <td className="py-1.5 pr-2">{fmtFechaHora(r.apertura)}</td><td className="py-1.5 pr-2">{fmtFechaHora(r.cierre)}</td><td className="py-1.5 pr-2">{r.cajero ?? '—'}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{money(Number(r.total_ventas ?? 0))}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{money(Number(r.efectivo_sistema ?? 0))}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{money(Number(r.efectivo_contado ?? 0))}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${Number(r.diferencia ?? 0) === 0 ? '' : 'font-semibold text-red-600'}`}>{money(Number(r.diferencia ?? 0))}</td>
                <td className="py-1.5 pr-3 text-right"><Button size="sm" variant="ghost" onClick={() => onAbrir(r.id)}><Printer className="mr-1 h-3.5 w-3.5" /> Z</Button></td>
              </tr>))}
          </tbody>
          {turnos.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">{turnos.length} turno(s)</td>
            <td className="py-2 pr-2 text-right tabular-nums">{money(tot.v)}</td><td colSpan={2} /><td className="py-2 pr-2 text-right tabular-nums">{money(tot.d)}</td><td /></tr></tfoot>}
        </table>
      </CardContent>
    </Card>
  );
}

function ReimpresionZ({ id, caja, onVolver }: { id: string; caja: string; onVolver: () => void }) {
  const { data: d, isLoading } = useQuery<Detalle>({ queryKey: [QK, 'turno-detalle', id], queryFn: () => tallerRpc('turno_detalle', { _sesion: id }) });
  return (
    <div className="mx-auto max-w-md space-y-3 p-4">
      <div className="no-print flex items-center gap-2"><Button size="sm" variant="outline" onClick={onVolver}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Button><PrintButton /></div>
      {isLoading || !d ? <div className="text-sm text-muted-foreground">Cargando…</div> : (
        <Imprimible titulo="Cierre de caja (Z)" subtitulo={`${d.codigo} · ${fmtFechaHora(d.cierre)}`}>
          <Resumen r={d} caja={caja} />
          <div className="mt-2 space-y-1 border-t pt-2 text-sm">
            {(d.arqueo ?? []).map((a, i) => <Linea key={i} k={`${a.cantidad} × ${money(Number(a.valor))} (${a.tipo})`} v={money(Number(a.valor) * a.cantidad)} />)}
            <Linea k="Efectivo contado" v={money(Number(d.efectivo_contado ?? 0))} fuerte />
            <Linea k="Efectivo esperado" v={money(Number(d.efectivo_sistema_cierre ?? 0))} />
            <div className={`flex justify-between font-bold ${Number(d.diferencia ?? 0) === 0 ? 'text-emerald-600' : 'text-red-600'}`}><span>Diferencia</span><span>{money(Number(d.diferencia ?? 0))}</span></div>
            {d.observaciones && <div className="text-xs text-muted-foreground">Obs.: {d.observaciones}</div>}
            <div className="pt-6 text-center text-xs">________________________<br />Firma del cajero</div>
          </div>
        </Imprimible>
      )}
    </div>
  );
}
